<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BrainIcon from 'dashboard/components-next/icon/BrainIcon.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const store = useStore();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const isBotToggling = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(
  () => store.getters['unifiedView/getEffectiveChat']
);

const inbox = computed(() => {
  const inboxId = currentChat.value.inbox_id;
  return inboxId ? store.getters['inboxes/getInbox'](inboxId) : {};
});

const hasBotConfigured = computed(() => !!inbox.value.active_bot);
const isBotActive = computed(() => !!currentChat.value.bot_active);

// Captain assistant info (Camada 1)
const captainAssistant = computed(() => inbox.value.captain_assistant || null);
const assistantStatus = computed(() => captainAssistant.value?.status || null);
const assistantName = computed(() => captainAssistant.value?.name || '');

// Camada 1 — assistant pode responder nesta conversa?
const assistantCanRespondHere = computed(() => {
  if (!captainAssistant.value) return true; // sem captain, mantém comportamento de outros bots
  if (assistantStatus.value === 'active') return true;
  if (assistantStatus.value === 'testing') {
    const testIds = captainAssistant.value.test_conversation_ids || [];
    return testIds.includes(currentChat.value.id);
  }
  return false; // draft, paused
});

// Estado consolidado pra renderização do botão
const botUiState = computed(() => {
  if (!hasBotConfigured.value) return 'no_bot';

  if (assistantStatus.value === 'draft') return 'assistant_draft';
  if (assistantStatus.value === 'paused') return 'assistant_paused_global';

  if (assistantStatus.value === 'testing') {
    if (!assistantCanRespondHere.value) return 'testing_excluded';
    return isBotActive.value ? 'testing_active' : 'testing_paused_here';
  }

  // status === 'active' ou null (bot tradicional)
  return isBotActive.value ? 'active_on' : 'active_paused_here';
});

const botTooltip = computed(() => {
  switch (botUiState.value) {
    case 'no_bot':
      return t('CONVERSATION.BOT_TOGGLE.NO_BOT');
    case 'assistant_draft':
      return `${assistantName.value} está em edição. Não responde.`;
    case 'assistant_paused_global':
      return `${assistantName.value} está pausado globalmente.`;
    case 'testing_excluded':
      return `${assistantName.value} está em modo teste. Esta conversa não está na lista.`;
    case 'testing_paused_here':
      return `Bot em teste. Pausado aqui — clique para reativar.`;
    case 'testing_active':
      return `Bot em teste — clique para pausar nesta conversa.`;
    case 'active_paused_here':
      return `Bot pausado nesta conversa — clique para reativar.`;
    case 'active_on':
      return `Bot ativo — clique para pausar nesta conversa.`;
    default:
      return '';
  }
});

const botLabel = computed(() => {
  switch (botUiState.value) {
    case 'assistant_draft':
      return 'Edição';
    case 'assistant_paused_global':
      return 'Pausado';
    case 'testing_excluded':
      return 'Bot';
    case 'testing_paused_here':
      return 'Pausado';
    case 'testing_active':
      return 'Teste';
    case 'active_paused_here':
      return 'Pausado';
    case 'active_on':
      return 'Bot';
    default:
      return 'Bot';
  }
});

const botCssClass = computed(() => {
  switch (botUiState.value) {
    case 'no_bot':
      return 'brain-toggle--disabled';
    case 'assistant_draft':
      return 'brain-toggle--draft';
    case 'assistant_paused_global':
    case 'testing_excluded':
      return 'brain-toggle--disabled';
    case 'testing_paused_here':
    case 'active_paused_here':
      return 'brain-toggle--off';
    case 'testing_active':
      return 'brain-toggle--testing';
    case 'active_on':
      return 'brain-toggle--on';
    default:
      return 'brain-toggle--disabled';
  }
});

const botButtonDisabled = computed(() => {
  return [
    'no_bot',
    'assistant_draft',
    'assistant_paused_global',
    'testing_excluded',
  ].includes(botUiState.value);
});

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const toggleBotStatus = async () => {
  isBotToggling.value = true;
  try {
    await store.dispatch('toggleBot', {
      conversationId: currentChat.value.id,
    });
    const label = currentChat.value.bot_active
      ? t('CONVERSATION.BOT_TOGGLE.ACTIVATED')
      : t('CONVERSATION.BOT_TOGGLE.DEACTIVATED');
    useAlert(label);
  } catch {
    useAlert(t('CONVERSATION.BOT_TOGGLE.FAILED'));
  } finally {
    isBotToggling.value = false;
  }
};

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = async (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  try {
    await store.dispatch('toggleStatus', payload);
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  } catch {
    useAlert(t('CONVERSATION.CHANGE_STATUS_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center gap-1.5 resolve-actions">
    <button
      v-tooltip="botTooltip"
      class="brain-toggle"
      :class="botCssClass"
      :disabled="botButtonDisabled || isBotToggling"
      @click="toggleBotStatus"
    >
      <BrainIcon :size="16" class="brain-toggle__icon" />
      <span class="brain-toggle__label">{{ botLabel }}</span>
      <span v-if="hasBotConfigured" class="brain-toggle__indicator" />
    </button>
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow-sm outline-1 outline outline-transparent"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        icon="i-lucide-circle-check-big"
        size="xs"
        color="slate"
        variant="faded"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        icon="i-lucide-inbox"
        size="xs"
        color="amber"
        variant="faded"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        icon="i-lucide-inbox"
        size="xs"
        color="blue"
        variant="faded"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="xs"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        :color="isOpen ? 'slate' : isResolved ? 'amber' : 'blue'"
        :variant="isOpen ? 'faded' : 'faded'"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>

<style scoped>
.brain-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px 4px 6px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1.5px solid;
  background: transparent;
  white-space: nowrap;
}

.brain-toggle__icon {
  flex-shrink: 0;
  border-radius: 4px;
}

.brain-toggle__label {
  line-height: 1;
}

.brain-toggle__indicator {
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  flex-shrink: 0;
  transition: all 0.2s;
}

/* ON state — green accent */
.brain-toggle--on {
  border-color: rgb(var(--teal-9));
  color: rgb(var(--teal-9));
  background: rgb(var(--teal-3));
}

.brain-toggle--on .brain-toggle__indicator {
  background: rgb(var(--teal-9));
  box-shadow: 0 0 4px rgb(var(--teal-9) / 0.5);
}

/* OFF state — red accent */
.brain-toggle--off {
  border-color: rgb(var(--ruby-7));
  color: rgb(var(--ruby-9));
  background: rgb(var(--ruby-3));
}

.brain-toggle--off .brain-toggle__indicator {
  background: rgb(var(--ruby-9));
  box-shadow: 0 0 4px rgb(var(--ruby-9) / 0.5);
}

/* Testing state — purple accent */
.brain-toggle--testing {
  border-color: rgb(var(--violet-9));
  color: rgb(var(--violet-9));
  background: rgb(var(--violet-3));
}

.brain-toggle--testing .brain-toggle__indicator {
  background: rgb(var(--violet-9));
  box-shadow: 0 0 4px rgb(var(--violet-9) / 0.5);
}

/* Draft state — yellow accent */
.brain-toggle--draft {
  border-color: rgb(var(--amber-9));
  color: rgb(var(--amber-9));
  background: rgb(var(--amber-3));
  cursor: not-allowed;
}

.brain-toggle--draft .brain-toggle__indicator {
  background: rgb(var(--amber-9));
}

/* No bot configured / Paused globally — grey */
.brain-toggle--disabled {
  border-color: rgb(var(--slate-6));
  color: rgb(var(--slate-8));
  background: rgb(var(--slate-2));
  opacity: 0.6;
  cursor: not-allowed;
}

.brain-toggle:disabled {
  cursor: not-allowed;
}
</style>
